import { Link } from "@tanstack/react-router";
import { Menu, Instagram } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useLang } from "@/hooks/use-language";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import mascot from "@/assets/mascot.png";
import { SOCIAL } from "@/lib/social-links";
import { TikTokIcon, FacebookIcon } from "@/components/icons/SocialIcons";
import { TAGLINE } from "@/lib/brand";

/** Top-level navigation shown on every brand/company page. */
export const PRIMARY_NAV = [
  { to: "/about", en: "About", ar: "من نحن" },
  { to: "/story", en: "Our Story", ar: "قصتنا" },
  { to: "/vision", en: "Vision & Message", ar: "الرؤية والرسالة" },
  { to: "/why", en: "Why Jozori?", ar: "لماذا جذوري؟" },
  { to: "/founder", en: "Founder", ar: "المؤسس" },
  { to: "/safety", en: "Parent Safety", ar: "أمان الأطفال" },
  { to: "/contact", en: "Contact", ar: "تواصل معنا" },
] as const;

interface Props {
  children: ReactNode;
  /** Optional active path for highlighting, e.g. "/about". */
  active?: string;
}

export function SiteShell({ children, active }: Props) {
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";
  const [open, setOpen] = useState(false);
  const portal = isAr ? "بوابة الأهل" : "Parent Portal";

  return (
    <div dir={dir} className="min-h-dvh bg-background text-foreground">
      {/* HEADER */}
      <header className="web-only sticky top-0 z-40 bg-background/85 backdrop-blur border-b border-clay/30">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
          >
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span className="font-display-ar text-xl font-black text-primary">جذوري</span>
            <span className="hidden sm:inline font-display-en text-base font-extrabold tracking-tight">JOZORI</span>
          </Link>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-6">
            {PRIMARY_NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-semibold transition-colors hover:text-primary ${
                  active === l.to ? "text-primary" : "text-foreground/70"
                }`}
              >
                {isAr ? l.ar : l.en}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              aria-label={isAr ? "English" : "العربية"}
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20 min-h-9"
            >
              {isAr ? "EN" : "AR"}
            </button>
            <Link
              to="/login"
              className="hidden sm:inline-flex items-center rounded-full bg-secondary text-secondary-foreground px-4 py-2 text-sm font-bold shadow-sm hover:scale-105 transition-transform min-h-10"
            >
              {portal}
            </Link>

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={isAr ? "القائمة" : "Menu"}
                  className="lg:hidden size-10 inline-flex items-center justify-center rounded-xl bg-card border border-clay/40 hover:bg-clay/20 transition-colors"
                >
                  <Menu className="size-5" aria-hidden="true" />
                </button>
              </SheetTrigger>
              <SheetContent
                side={isAr ? "left" : "right"}
                className="bg-background w-[88vw] sm:w-96 flex flex-col"
              >
                <SheetHeader>
                  <SheetTitle className="font-display-en text-2xl tracking-tight">
                    {isAr ? "القائمة" : "Menu"}
                  </SheetTitle>
                </SheetHeader>
                <nav aria-label="Mobile" className="mt-6 flex flex-col gap-1 overflow-y-auto">
                  <Link
                    to="/"
                    onClick={() => setOpen(false)}
                    className="px-4 py-3.5 rounded-xl text-base font-bold hover:bg-clay/20 transition-colors"
                  >
                    {isAr ? "الرئيسية" : "Home"}
                  </Link>
                  {PRIMARY_NAV.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      className={`px-4 py-3.5 rounded-xl text-base font-bold transition-colors ${
                        active === l.to
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-clay/20"
                      }`}
                    >
                      {isAr ? l.ar : l.en}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pt-6 border-t border-clay/30 space-y-2">
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="block bg-primary text-primary-foreground px-5 py-3.5 rounded-2xl text-center font-bold shadow-press"
                  >
                    {portal}
                  </Link>
                  <a
                    href="mailto:info@jozori.com"
                    className="block text-center text-xs font-semibold text-foreground/60 hover:text-primary py-2"
                  >
                    info@jozori.com
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main>{children}</main>

      {/* FOOTER */}
      <footer className="web-only bg-foreground text-background mt-20 px-5 py-14">
        <div className="max-w-6xl mx-auto grid gap-10 md:grid-cols-5">
          <div className="md:col-span-2">
            <div className="font-display-en text-2xl font-black mb-3 tracking-tighter">
              JOZORI / جذوري
            </div>
            <p
              className={`max-w-[34ch] opacity-60 text-sm ${isAr ? "font-display-ar text-base" : ""}`}
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr
                ? "نبني جيلاً متجذراً بالقيم، والثقة، والانتماء."
                : "Building a generation rooted in values, confidence, and belonging."}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={SOCIAL.instagram.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-background/20 bg-background/10 px-3 py-1.5 text-xs font-bold hover:bg-background/20 transition-colors"
              >
                <Instagram className="size-3.5" />
                <span>Instagram</span>
              </a>
              <a
                href={SOCIAL.tiktok.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-background/20 bg-background/10 px-3 py-1.5 text-xs font-bold hover:bg-background/20 transition-colors"
              >
                <TikTokIcon className="size-3.5" />
                <span>TikTok</span>
              </a>
              <a
                href={SOCIAL.facebook.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-background/20 bg-background/10 px-3 py-1.5 text-xs font-bold hover:bg-background/20 transition-colors"
              >
                <FacebookIcon className="size-3.5" />
                <span>Facebook</span>
              </a>
            </div>

            <address className="not-italic mt-5 space-y-1.5 text-sm opacity-80" dir={isAr ? "rtl" : "ltr"}>
              <div className="font-bold opacity-100">
                {isAr ? "المقر الرئيسي" : "Headquarters"}
              </div>
              <div>
                {isAr
                  ? "تماني آرتس أوفيسز، مكتب ١٥١١"
                  : "Tamani Arts Offices, Office 1511"}
              </div>
              <div>
                {isAr
                  ? "الخليج التجاري، دبي، الإمارات"
                  : "Business Bay, Dubai, UAE"}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                <a href="mailto:info@jozori.com" className="hover:opacity-100 underline-offset-4 hover:underline">
                  info@jozori.com
                </a>
                <a
                  href="https://maps.google.com/maps/place//data=!4m2!3m1!1s0x3e5f69268414659b:0x57b0c10714f75064"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-100 underline-offset-4 hover:underline"
                >
                  {isAr ? "الخريطة" : "View map"}
                </a>
              </div>
            </address>
          </div>
          <FooterCol
            title={isAr ? "الشركة" : "Company"}
            links={[
              { to: "/about", label: isAr ? "من نحن" : "About" },
              { to: "/story", label: isAr ? "قصتنا" : "Our Story" },
              { to: "/vision", label: isAr ? "الرؤية والرسالة" : "Vision & Message" },
              { to: "/founder", label: isAr ? "المؤسس" : "Founder" },
            ]}
          />
          <FooterCol
            title={isAr ? "المنصة" : "Platform"}
            links={[
              { to: "/why", label: isAr ? "لماذا جذوري؟" : "Why Jozori?" },
              { to: "/safety", label: isAr ? "أمان الأطفال" : "Parent Safety" },
              { to: "/contact", label: isAr ? "تواصل معنا" : "Contact" },
              { to: "/login", label: isAr ? "تسجيل الدخول" : "Sign in" },
            ]}
          />
          <FooterCol
            title={isAr ? "قانوني" : "Legal"}
            links={[
              { to: "/privacy", label: isAr ? "الخصوصية" : "Privacy" },
              { to: "/terms", label: isAr ? "الشروط" : "Terms" },
              { to: "/age-rating", label: isAr ? "الفئة العمرية" : "Age Rating" },
              { to: "/refunds", label: isAr ? "الاسترداد" : "Refunds" },
            ]}
          />
        </div>
        <div className="max-w-6xl mx-auto mt-12 pt-6 border-t border-background/10 text-[10px] uppercase tracking-widest opacity-30 flex flex-wrap justify-between gap-3">
          <span>
            {isAr
              ? "© ٢٠٢٦ جذوري الدولية. صُنع بحب لأطفال المهجر."
              : "© 2026 Jozori International. Handcrafted for the diaspora."}
          </span>
          <span className="normal-case tracking-normal opacity-90" dir="rtl">
            <span className="font-display-ar text-sm">{TAGLINE.ar}</span>
            <span className="opacity-60"> · </span>
            <span className="font-display-en text-[11px] italic">{TAGLINE.en}</span>
          </span>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string }[];
}) {
  return (
    <div className="space-y-3">
      <h4 className="font-bold text-[10px] uppercase tracking-widest opacity-40">{title}</h4>
      <nav className="flex flex-col gap-2 text-sm font-medium">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="opacity-80 hover:opacity-100 transition-opacity"
          >
            {l.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
